import {
  ADD,
  ADD_ONS,
  FULL_SIZE,
  HALF_SIZE,
  ITEM_AS_MODIFIERS,
  NO,
  REQUIRED_MODIFIERS,
} from 'src/constants';
import {
  IItemsRequiredModifierGroups,
  IRequiredModifiersGroupsModifiers,
  ISubCategories,
  ItemDetails,
  subITemsAsMod,
} from 'src/models/item.model';
import { IAddToOrderPayload } from 'src/models/order.model';
import { IBerryRecommendedItem } from 'src/models/berry.model';

/**
 * `getLocationMenuItem` returns `sub_items_required_modifier_groups` (typed as
 * `subITemsAsMod[]` in item.model.ts) whose `items_as_modifier` entries carry a
 * few fields that are real on the wire but not modeled on `ItemAsModifier`
 * (confirmed via src/helper/sandwichTrayChips.helper.ts and
 * src/containers/Modifiers/ItemsAsModifiers.tsx, which read them off `any`).
 * Declared locally rather than widening the shared model file.
 */
export interface IBerrySubItemModifier {
  id: number;
  name: string;
  image?: string;
  price: number;
  half_brink_item_id?: number | null;
  full_brink_item_id?: number;
  half_brink_item_price?: number;
  /** Default pick flag — when true this option is part of the tray's default fill. */
  is_selected_with_count?: number | boolean;
  /** Default quantity-in-increments for the HALF size (see mapSizesKey). */
  size_one_count?: number;
  /** Default quantity-in-increments for the FULL size. */
  size_two_count?: number;
}

export interface IBerrySubItemGroup extends Omit<subITemsAsMod, 'items_as_modifier'> {
  items_as_modifier: IBerrySubItemModifier[];
  /** Real per-group increment field (see ItemsAsModifiers.tsx / sandwichTrayChips.helper.ts). */
  item_counter_increment?: number | null;
  half_item_count?: number | null;
  full_item_count?: number | null;
}

export type BerrySize = 'HALF' | 'FULL';

export type BerryItemFamily =
  | 'sandwich_tray'
  | 'soup_or_salad_tray'
  | 'package'
  | 'box_lunch';

export interface IBerryRequiredChoice {
  groupId: number;
  brinkModifierGroupId: number;
  modifierId: number;
  brinkModifierId: number;
  name: string;
  price: number;
  /** Real fields confirmed against a working production request (bread/chips/
   * dessert-drink modifiers) — pulled straight off the group/modifier objects
   * rather than invented. */
  image?: string;
  calories?: number | null;
  inItem: number;
  groupMax: number;
  groupBase: number;
  extendableLimit: number;
  /** What this choice adds to the item (requiredChoiceDelta) — display/total only. */
  delta?: number;
}

export interface IBerrySubItemChoice {
  groupId: number;
  itemId: number;
  name: string;
  /** Current quantity — never forced to 0 for a removed default; see
   * subItemModifier's `code` derivation below for why. */
  quantity: number;
  unitPrice: number;
  /** Resolved via resolveSubItemModifierSize against the pick's *parent tray*
   * item — not the tray size the user chose. Carried here because
   * buildCartItemFromSelection has no visibility into that parent item. */
  modifierSize: BerrySize;
  /**
   * The option's default quantity at this tray's size (0 if it was never a
   * default). Confirmed real rule (see OtherManuItems' `modifierCode`):
   * `code` is "No" whenever the *current* quantity is less than this — not
   * just when it's been zeroed out — and the entry is still sent with
   * quantity:0 if this is fully zero.
   */
  defaultQuantity: number;
  /** Counter step (2 halves for sandwiches) and what each step adds
   * (subItemDeltaPerIncrement) — display/total only. */
  increment?: number;
  deltaPerIncrement?: number;
}

export interface IBerryPackageCategoryChoice {
  categoryId: number;
  categoryName: string;
  manual: boolean;
  chosenItem: ItemDetails | null;
  /**
   * Populated when `chosenItem` is itself a configurable tray (e.g. item 319
   * nested inside item 324's "Sandwich Trays" category) — its own bread/chips
   * picks and sandwich/soup sub-item picks. Kept separate from the top-level
   * `requiredChoices`/`subItems` so the cart payload can nest them under this
   * category's own modifier node (confirmed real shape — see
   * buildCartItemFromSelection). Undefined for a plain leaf category (salad
   * tray) with no nested groups.
   */
  nestedItemId?: number;
  nestedRequiredChoices?: IBerryRequiredChoice[];
  nestedSubItems?: IBerrySubItemChoice[];
  /** What the chosen item adds (pickDelta) — display/total only. */
  delta?: number;
}

export interface IBerryPackageOptionalChoice {
  groupId: number;
  choice: IBerryRequiredChoice | null;
}

/**
 * An optional add-on counted per serving — a Soup Tray's "Soup Mix-Ins"
 * (`items_modifier_groups`: Crackers $0, Daily's Bacon $1.49 …, each with
 * its own max). Matches the entry the site's own item page sends (captured
 * from a real soup-tray add on 2026-10-06): modifier_type "add_ons", code
 * "ADD", the real quantity, display_price/additionalPrice = unit price.
 */
export interface IBerryAddOnChoice {
  groupId: number;
  modifierId: number;
  brinkModifierId: number;
  name: string;
  price: number;
  quantity: number;
  max: number;
  image?: string;
  calories?: number | null;
  groupMax: number;
  groupBase: number;
  extendableLimit: number;
}

export interface IBerryItemSelection {
  family: BerryItemFamily;
  itemId: number;
  categoryId: string;
  name: string;
  size: BerrySize | null;
  brinkItemId: number;
  /** Unit price — not multiplied by `quantity`. Pulse's own `quantity` field
   * carries the multiplier, matching how sub-item picks already report a
   * per-unit `display_price` alongside a separate `quantity`. */
  price: number;
  /** How many of this whole item (tray/package/simple item) the customer
   * wants — chosen on the picker's Review step, defaults to 1. */
  quantity: number;
  requiredChoices: IBerryRequiredChoice[];
  subItems: IBerrySubItemChoice[];
  packageCategories: IBerryPackageCategoryChoice[];
  packageOptional: IBerryPackageOptionalChoice[];
  /** Optional per-serving add-ons (soup mix-ins). */
  addOns?: IBerryAddOnChoice[];
  /** Salad trays: the cart line built by the site's own customization
   * engine (helper/siteItemCustomization) — sent as-is instead of
   * buildCartItemFromSelection. `price` is then the customized unit price. */
  siteCartItem?: Record<string, any>;
  /** Salad trays: readable changes for the review step. */
  siteChanges?: string[];
}

/** True when a recommended-item card needs the in-panel picker instead of a direct add. */
export function isConfigurableBerryItem(item: IBerryRecommendedItem): boolean {
  return Boolean(
    item.is_package ||
      item.is_box_lunch ||
      item.is_sandwich_tray ||
      item.is_soup_tray ||
      item.is_salad_tray,
  );
}

export function getBerryItemFamily(item: {
  is_package?: boolean | number;
  is_box_lunch?: boolean | number;
  is_sandwich_tray?: boolean | number;
  is_soup_tray?: boolean | number;
  is_salad_tray?: boolean | number;
}): BerryItemFamily {
  // Box lunches route to their own flow: they carry `sub_categories` (a
  // sandwich list + a soup list) rather than `package_categories`, plus an
  // "I'll Choose" (HALF) / "Choose For Me" (FULL) size toggle. Checked first
  // since a box lunch's tray flags are all 0 anyway.
  if (item.is_box_lunch) return 'box_lunch';
  if (item.is_sandwich_tray) return 'sandwich_tray';
  if (item.is_soup_tray || item.is_salad_tray) return 'soup_or_salad_tray';
  return 'package';
}

/** Size step only shows when both half/full counts are present on the fetched item. */
export function hasSizeToggle(item: ItemDetails): boolean {
  return Boolean(item.half_item_count) && Boolean(item.full_item_count);
}

/**
 * The size Customize opens at: Berry's suggestion for the group (UAT row 12 —
 * "it wanted me to select Small vs Large; shouldn't it just take me to the
 * large?"). Only for a two-size tray (never a box lunch's I'll Choose /
 * Choose For Me, never a package), and only a size this store's item has.
 */
export function suggestedTraySize(
  item: Pick<IBerryRecommendedItem, 'suggested_size'>,
  fetched: ItemDetails | null | undefined,
  family: BerryItemFamily,
): BerrySize | null {
  const suggested = item.suggested_size;
  if (!fetched || (suggested !== HALF_SIZE.id && suggested !== FULL_SIZE.id)) return null;
  if (family !== 'sandwich_tray' && family !== 'soup_or_salad_tray') return null;
  if (!hasSizeToggle(fetched)) return null;
  if (suggested === HALF_SIZE.id && !fetched.half_brink_item_id) return null;
  if (suggested === FULL_SIZE.id && !(fetched.full_brink_item_id ?? fetched.id)) return null;
  return suggested as BerrySize;
}

/** "1 Large + 1 Small" — Berry's size plan as the customer reads it. */
export function sizePlanLabel(plan: IBerryRecommendedItem['size_plan']): string | null {
  if (!plan || plan.length === 0) return null;
  return plan
    .map((p) => `${p.quantity} ${p.size === HALF_SIZE.id ? 'Small' : 'Large'}`)
    .join(' + ');
}

export function sizeToMapKey(size: BerrySize): 'size_one_count' | 'size_two_count' {
  return size === HALF_SIZE.id ? 'size_one_count' : 'size_two_count';
}

/** Total quantity target for the sub-item counter step, based on the chosen size. */
export function getSizeTotalTarget(
  item: ItemDetails,
  group: IBerrySubItemGroup | null,
  size: BerrySize | null,
): number | null {
  if (!size) return null;
  const half = group?.half_item_count ?? item.half_item_count;
  const full = group?.full_item_count ?? item.full_item_count;
  return size === HALF_SIZE.id ? half ?? null : full ?? null;
}

export function resolveSizeBrinkItemId(item: ItemDetails, size: BerrySize | null): number {
  if (size === HALF_SIZE.id) return item.half_brink_item_id;
  if (size === FULL_SIZE.id) return item.full_brink_item_id;
  return item.full_brink_item_id ?? item.half_brink_item_id;
}

export function resolveSizePrice(item: ItemDetails, size: BerrySize | null): number {
  if (size === HALF_SIZE.id) return item.half_brink_item_price;
  return item.price;
}

/** Default quantity (actual units, not increments) for a sub-item option at a given size. */
export function defaultSubItemQuantity(
  option: IBerrySubItemModifier,
  group: IBerrySubItemGroup,
  size: BerrySize,
): number {
  if (!option.is_selected_with_count) return 0;
  const increment = group.item_counter_increment || 1;
  const countKey = sizeToMapKey(size);
  const count = option[countKey] ?? 0;
  return count * increment;
}

/**
 * The size reported on a sub-item pick's cart-write modifier — NOT the tray
 * size the user chose (12 vs 24). Confirmed real rule (see
 * OtherManuItems/index.tsx's `setSize`): a sandwich/soup pick is always
 * "HALF" whenever its parent tray supports half-sizing at all (soup trays
 * unconditionally; sandwich trays via `half_brink_item_id`), regardless of
 * whether the tray itself was configured as a half or full tray. Only a tray
 * with no half option at all reports FULL for its picks.
 */
export function resolveSubItemModifierSize(
  parentTrayItem: Pick<ItemDetails, 'half_brink_item_id'> & { is_soup_tray?: number | boolean },
): BerrySize {
  if (parentTrayItem.is_soup_tray) return 'HALF';
  if (parentTrayItem.half_brink_item_id) return 'HALF';
  return 'FULL';
}

/**
 * Whether a package category requires the user to actively pick an item
 * (vs. being auto-included and non-editable).
 *
 * The real per-category signal is the API's `manual_selection` flag
 * (`1` = user must pick, `0` = auto-included) — per the production
 * frontend's own documented handling of `getLocationMenuItem`. When that flag
 * is present on the category we honor it directly.
 *
 * Fallback: some payloads (and all of the fixtures captured before this flag
 * was confirmed) don't carry `manual_selection`; there we keep the original
 * `items.length > 1` heuristic, which the production container itself uses at
 * runtime (see src/containers/OtherManuItems/index.tsx's
 * `package_categories.filter(tray => tray.items.length > 1)` vs the `=== 1`
 * auto-include branch). So behavior is unchanged where the flag is absent and
 * correct wherever it's present.
 */
export function isManualPackageCategory(
  category: ISubCategories & { manual_selection?: number | boolean | null },
): boolean {
  const manual = category.manual_selection;
  if (manual != null) {
    return Number(manual) === 1;
  }
  return (category.items?.length ?? 0) > 1;
}

/**
 * Sum of all selected quantities in a sub-item group. There is no cap on how
 * many *distinct* kinds a user can pick (confirmed against a real production
 * order with 5 distinct sandwich kinds, despite the group's own `max:4` field
 * — that field is not a "distinct kinds" limit; do not gate on it). The only
 * real constraint, per sub-item and for the group as a whole alike, is that
 * the running total can't exceed the size's total count target — a single
 * item is bounded by that same total since it can never exceed the sum it's
 * part of.
 */
export function sumGroupQuantities(quantities: Record<number, number>): number {
  return Object.values(quantities).reduce((acc, v) => acc + v, 0);
}

/** Whether an option can go up by one increment without pushing the group's
 * running total past the size's total count target. */
export function canIncrementSubItem(params: {
  group: IBerrySubItemGroup;
  totalTarget: number | null;
  currentTotal: number;
}): boolean {
  const { group, totalTarget, currentTotal } = params;
  const increment = group.item_counter_increment || 1;
  return !(totalTarget != null && currentTotal + increment > totalTarget);
}

/**
 * A required-choice leaf (bread, chips, dessert/drink upsell). Confirmed
 * field-for-field against a working production request: no size/item_size/
 * modifier_size at all on this type, no brink_modifier_group_id, and `code`
 * is the literal "ADD" (all-caps) — a different literal than the "Add"/"No"
 * used for item-as-modifier entries (see subItemModifier), because this
 * shape comes from a different part of the real pipeline (the standard
 * required-modifier picker, not addItemsAsModifierInPayload).
 */
function requiredChoiceModifier(choice: IBerryRequiredChoice) {
  return {
    modifier_id: choice.modifierId,
    modifier_name: choice.name,
    display_price: choice.price,
    modifier_calories: choice.calories ?? null,
    modifier_group_min: 1,
    brink_id: choice.brinkModifierId,
    type: 'selected',
    image: choice.image,
    code: 'ADD',
    extendableLimitValue: choice.extendableLimit,
    modifier_group_id: choice.groupId,
    modifier_group_max: choice.groupMax,
    modifier_group_base: choice.groupBase,
    in_item: choice.inItem,
    in_slide: true,
    modifier_type: REQUIRED_MODIFIERS,
    quantity: 1,
    previousPrice: 0,
    additionalPrice: 0,
    is_item: false,
  };
}

function addOnModifier(addOn: IBerryAddOnChoice) {
  return {
    modifier_id: addOn.modifierId,
    modifier_name: addOn.name,
    display_price: addOn.price,
    modifier_calories: addOn.calories ?? null,
    brink_id: addOn.brinkModifierId,
    quantity: addOn.quantity,
    modifier_group_min: 0,
    max: addOn.max,
    type: 'increase',
    is_selected: 0,
    image: addOn.image,
    code: 'ADD',
    extendableLimitValue: addOn.extendableLimit,
    modifier_group_id: addOn.groupId,
    modifier_group_max: addOn.groupMax,
    modifier_group_base: addOn.groupBase,
    modifier_type: ADD_ONS,
    additionalPrice: addOn.price,
    is_item: false,
  };
}

/**
 * A sub-item pick (sandwich/soup/salad kind). Confirmed field-for-field
 * against a working production request: no brink_id, no
 * brink_modifier_group_id, no item_size (only size + modifier_size, both
 * the pick's own HALF/FULL per resolveSubItemModifierSize — not the tray
 * size), `isCore` not `core`.
 *
 * `code`/`quantity` logic (confirmed via a real payload where a reduced-but-
 * not-zeroed default still showed `code:"No"` with its actual reduced
 * quantity, matching OtherManuItems' `modifierCode`): `quantity` sent is
 * always the real current quantity — never forced to 0. `code` is "No"
 * whenever that quantity is less than the option's original default
 * quantity (covers both partial reduction and full removal to zero) —
 * "Add" otherwise, including for a newly-picked non-default option.
 */
function subItemModifier(sub: IBerrySubItemChoice) {
  return {
    modifier_id: sub.itemId,
    modifier_name: sub.name,
    modifier_group_id: sub.groupId,
    display_price: sub.unitPrice,
    isCore: sub.defaultQuantity > 0,
    quantity: sub.quantity,
    code: sub.quantity < sub.defaultQuantity ? NO : ADD,
    modifier_type: ITEM_AS_MODIFIERS,
    size: sub.modifierSize,
    is_item: true,
    modifier_size: sub.modifierSize,
  };
}

/** Builds the flat Modifier[] + Item shape the existing cart-write path already
 * accepts (see useBerryAddToCart / BerryChatWidget.handleAddToCart) from a
 * completed picker selection. Reuses `IAddToOrderPayload['items']` — the type
 * the rest of Berry's cart-write call already casts to — rather than inventing
 * a new payload shape. Does not call any cart-write function itself.
 *
 * Package shape confirmed field-for-field against a working production
 * request for the same package type (see useAddToOrder.tsx →
 * createOrderPayloadModifiers, and OtherManuItems/index.tsx's
 * addToOrderHandler/addItemsAsModifierInPayload/setSize for how production
 * builds it): every `package_categories` entry — configurable tray or plain
 * leaf alike — shares one shape (`item`/`category_id`/`id`/`item_id`/
 * `modifier_id`/`name`/`price`/`modifiers[]`), differing only in whether
 * `modifiers[]` is populated. A configurable tray (e.g. item 319 nested in
 * item 324) additionally carries `basePrice` (its own full price) and a
 * populated `modifiers[]` of its own bread/chips/sandwich picks.
 */
/**
 * One sub-category pick (the chosen sandwich or soup) inside a box lunch.
 * Confirmed field-for-field against a working box-lunch add-to-cart request:
 * every pick carries `basePrice` and a per-item `size`/`modifier_size`
 * (unlike a package-category leaf) — HALF when the item has a half option,
 * else FULL (soup 28 → HALF/5.99; sandwich 2 → FULL/8.39). `code` is the
 * "Add" literal (item-as-modifier casing). Its own required groups (a
 * sandwich's bread; a soup has none) nest under `modifiers`.
 */
function boxLunchPickNode(cat: IBerryPackageCategoryChoice, slotIndex: string) {
  const pick = cat.chosenItem as (ItemDetails & { half_brink_item_id?: number | null; half_brink_item_price?: number });
  const pickSize: BerrySize = pick.half_brink_item_id ? 'HALF' : 'FULL';
  const basePrice = pick.half_brink_item_id ? pick.half_brink_item_price ?? pick.price : pick.price;
  return {
    item: slotIndex,
    category_id: pick.category_id,
    id: pick.id,
    name: pick.name,
    price: 0,
    modifiers: (cat.nestedRequiredChoices ?? []).map(requiredChoiceModifier),
    basePrice,
    size: pickSize,
    is_selected: true,
    item_id: pick.id,
    modifier_id: pick.id,
    modifier_name: pick.name,
    code: ADD,
    display_price: 0,
    modifier_size: pickSize,
    quantity: 1,
    is_item: true,
  };
}

/**
 * Box lunch cart item. Confirmed field-for-field against a working box-lunch
 * add-to-cart request: `item_size`/`brink_id` follow the HALF ("I'll Choose")
 * / FULL ("Choose For Me") toggle; `display_price`/`price`/`original_price`
 * are 0; `is_box_lunch` and `isTry2Combo` are true; `is_choose_for_me` is
 * true only for the FULL/kitchen-fills variant (no sub-category picks then).
 * `modifiers` = the sub-category picks (is_item:true) followed by the
 * required Dessert + Drink leaves (is_item:false), in that order.
 */
function buildBoxLunchCartItem(
  selection: IBerryItemSelection,
): NonNullable<IAddToOrderPayload['items']>[number] {
  const sizeLabel = selection.size ?? FULL_SIZE.id;
  const modifiers: any[] = [];

  selection.packageCategories.forEach((cat, index) => {
    if (!cat.chosenItem) return;
    modifiers.push(boxLunchPickNode(cat, String(index + 1)));
  });

  selection.packageOptional.forEach((opt) => {
    if (!opt.choice) return;
    modifiers.push(requiredChoiceModifier(opt.choice));
  });

  return {
    item_size: sizeLabel,
    item_id: selection.itemId,
    id: selection.itemId,
    name: selection.name,
    display_price: 0,
    price: 0,
    original_price: 0,
    item_calories: null,
    modifiers,
    brink_id: selection.brinkItemId,
    category_id: selection.categoryId,
    free: false,
    is_box_lunch: true,
    isTry2Combo: true,
    recipient_name: '',
    quantity: selection.quantity,
    special_instructions: '',
    is_choose_for_me: sizeLabel === FULL_SIZE.id,
    set_id: 1,
  } as unknown as NonNullable<IAddToOrderPayload['items']>[number];
}

export function buildCartItemFromSelection(
  selection: IBerryItemSelection,
): NonNullable<IAddToOrderPayload['items']>[number] {
  if (selection.family === 'box_lunch') return buildBoxLunchCartItem(selection);
  const isPackage = selection.family === 'package';
  // `selection.size` is null both for packages (no half/full split of their
  // own — see hasSizeToggle/showSizeStep) AND for any standalone tray with no
  // size toggle at all (e.g. a salad tray with a single flat price, no
  // half_item_count/full_item_count). Confirmed real rule (matches the same
  // fix already made for simple recommended items in
  // BerryChatWidget.handleAddToCart): item_size/size must never be sent as
  // "" — Pulse only recognizes real size tokens, and an empty string fails
  // the write. Fall back to FULL whenever there's no real size, regardless
  // of family, rather than special-casing packages only.
  const sizeLabel = selection.size ?? FULL_SIZE.id;
  const modifiers: any[] = [];

  selection.requiredChoices.forEach((choice) => {
    modifiers.push(requiredChoiceModifier(choice));
  });

  selection.subItems.forEach((sub) => {
    modifiers.push(subItemModifier(sub));
  });

  (selection.addOns ?? [])
    .filter((addOn) => addOn.quantity > 0)
    .forEach((addOn) => modifiers.push(addOnModifier(addOn)));

  // `item` is the category's 1-based slot position among package_categories
  // (confirmed real field — e.g. "1" for the first category, "2" for the
  // second) — present on every package_categories entry, leaf or node alike.
  selection.packageCategories.forEach((cat, index) => {
    if (!cat.chosenItem) return;
    const slotIndex = String(index + 1);

    const nestedModifiers: any[] = cat.nestedItemId != null
      ? [
          ...(cat.nestedRequiredChoices ?? []).map(requiredChoiceModifier),
          ...(cat.nestedSubItems ?? []).map(subItemModifier),
        ]
      : [];

    modifiers.push({
      item: slotIndex,
      category_id: cat.chosenItem.category_id,
      id: cat.chosenItem.id,
      name: cat.chosenItem.name,
      price: 0,
      modifiers: nestedModifiers,
      item_id: cat.chosenItem.id,
      modifier_id: cat.chosenItem.id,
      // Only configurable trays report their own base price alongside a
      // populated modifiers[] — a plain leaf (e.g. salad tray) has neither.
      ...(cat.nestedItemId != null ? { basePrice: cat.chosenItem.price } : {}),
      code: ADD,
      size: FULL_SIZE.id,
      display_price: 0,
      modifier_size: FULL_SIZE.id,
      quantity: 1,
      is_item: true,
    });
  });

  selection.packageOptional.forEach((opt) => {
    if (!opt.choice) return;
    modifiers.push(requiredChoiceModifier(opt.choice));
  });

  return {
    brink_id: selection.brinkItemId,
    category_id: selection.categoryId,
    display_price: selection.price,
    id: selection.itemId,
    index: 0,
    item_id: selection.itemId,
    item_size: sizeLabel,
    modifiers,
    name: selection.name,
    price: selection.price,
    quantity: selection.quantity,
    set_id: 1,
    size: sizeLabel,
    is_package: isPackage,
  } as NonNullable<IAddToOrderPayload['items']>[number];
}

// ── Price behavior (matches the production item pages) ──────────────────────
//
// Verified 2026-10-06 against catering.cafezupas.com's own item pages and the
// carts Pulse builds (see berry-catering-agent
// .claude/context/e2e-persona-test/bugs-log.md, "Production behavior audit").
// A choice's on-screen effect is what it ADDS to the item, never its
// stand-alone price:
//   - box-lunch pick (sandwich/salad/bowl)  → `extra_amount`   (Cobb +$2.00)
//   - required group (bread/chips/drink/dessert) → price − group.base
//       (box drinks/chips: base 0 → full price; Premium Package dessert:
//        base $40.38 = the included strawberries → Fresh Berry Tray +$20.21)
//   - tray sandwich/soup counter → (price − group.min_mod_price) per
//       increment (Hot Honey Brisket $9.39 vs $8.39 → +$1.00 per pair)
//   - package category (the salad tray in a package) → `extra_amount` or $0
// The cart payload is unchanged — Pulse prices the line from it correctly;
// these only drive labels and the picker's running total.

const money = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/** What a required-group choice adds to its item. */
export function requiredChoiceDelta(
  modifier: { price?: number | null },
  group: { base?: number | null },
): number {
  return money(Math.max(0, (modifier.price ?? 0) - (group.base ?? 0)));
}

/** What a box-lunch pick or package-category item adds. */
export function pickDelta(item: { extra_amount?: number | null } | null | undefined): number {
  return money(Math.max(0, item?.extra_amount ?? 0));
}

/** What one counter increment of a tray sandwich/soup adds. */
export function subItemDeltaPerIncrement(
  option: { price?: number | null },
  group: { min_mod_price?: number | null },
): number {
  if (group.min_mod_price == null) return 0;
  return money(Math.max(0, (option.price ?? 0) - group.min_mod_price));
}

/** "+$2.00" for a positive delta, "" otherwise (included / no charge). */
export function deltaLabel(delta: number): string {
  return delta > 0 ? `+$${delta.toFixed(2)}` : '';
}

/**
 * True when Pulse has switched this option/item off at the store
 * (`modifier_location` / `item_location` entry for the location with a
 * non-null `status`, e.g. "disable" / "Out Of Item/Ingredient"). Production
 * shows these as "Unavailable" and won't let them be picked; Pulse itself
 * accepts and charges them if sent.
 */
export function isUnavailableAt(
  entity: { modifier_location?: any[]; item_location?: any[] } | null | undefined,
  locationId: number | string | null | undefined,
): boolean {
  if (!entity || locationId == null) return false;
  const rows = [...(entity.modifier_location ?? []), ...(entity.item_location ?? [])];
  return rows.some(
    (row) => row && String(row.location_id) === String(locationId) && row.status != null,
  );
}

/** Per-unit price of a completed selection: the size/base price plus every
 * choice's delta. Multiply by `selection.quantity` for the line. */
export function selectionUnitPrice(selection: IBerryItemSelection): number {
  let total = selection.price;
  const addRequired = (choices: IBerryRequiredChoice[] | undefined) =>
    (choices ?? []).forEach((c) => {
      total += c.delta ?? 0;
    });
  const addSubItems = (subs: IBerrySubItemChoice[] | undefined) =>
    (subs ?? []).forEach((s) => {
      const increments = s.increment ? s.quantity / s.increment : s.quantity;
      total += (s.deltaPerIncrement ?? 0) * increments;
    });
  addRequired(selection.requiredChoices);
  addSubItems(selection.subItems);
  selection.packageCategories.forEach((cat) => {
    total += cat.delta ?? 0;
    addRequired(cat.nestedRequiredChoices);
    addSubItems(cat.nestedSubItems);
  });
  selection.packageOptional.forEach((opt) => {
    if (opt.choice) total += opt.choice.delta ?? 0;
  });
  (selection.addOns ?? []).forEach((a) => {
    total += (a.price ?? 0) * a.quantity;
  });
  return money(total);
}

/** Line total (unit price × quantity), rounded to cents. */
export function selectionLineTotal(selection: IBerryItemSelection): number {
  return money(selectionUnitPrice(selection) * selection.quantity);
}

export type { IItemsRequiredModifierGroups, IRequiredModifiersGroupsModifiers, ISubCategories };
